import { yupResolver } from '@hookform/resolvers';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TuneIcon from '@material-ui/icons/Tune';
import { Autocomplete } from '@material-ui/lab';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import InputCheckbox from '../../../../../../components/Inputs/Checkbox';
import DialogTitleBlock from '../../../../../../components/Modals/Dialog/DialogTitleBlock';
import { api } from '../../../../../../core/api';
import { resolveCompany } from '../../../../../../core/api/benchmark';
import { ResolveBenchmarkCompanyRequest } from '../../../../../../generated/model/resolveBenchmarkCompanyRequest';
import { useCompaniesState } from '../../../../../FinancialData/shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkState } from '../../../../Context/BenchmarkContext';

interface OwnProps {
  show: boolean;
  isEditing: boolean;
  title: string;
  comment: string | undefined;
  resolutionStatus: ResolveBenchmarkCompanyRequest.StatusEnum;
  handleSave: () => void;
  handleClose: () => void;
}

interface ResolutionForm {
  resolutionComment: string;
  applyForRemaining: boolean;
}

const rejectedReasonList = [
  'Reasonable doubt regarding company independence',
  'Insufficient information (website unavailable or uninformative)',
  'Significant difference in function(-s)',
  'Significant difference in product(-s)',
];

const ResolutionModal: React.FC<OwnProps> = ({
  show,
  isEditing,
  title,
  comment,
  resolutionStatus,
  handleClose,
  handleSave,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { activeBenchmark } = useBenchmarkState();
  const { company } = useCompaniesState();
  const dispatchErrors = useErrorsDispatch();

  const { handleSubmit, errors, control, clearErrors, register } = useForm<ResolutionForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema(resolutionStatus)),
  });

  useEffect(() => {
    if (!show) {
      return setOptions([]);
    }
    (async () => {
      try {
        setLoading(true);
        const comments = await api.benchmark.fetchBenchmarkComments(activeBenchmark?.id!, {
          status: resolutionStatus,
        });
        if (resolutionStatus === 'REJECTED') {
          setOptions(_.union(rejectedReasonList, comments));
        } else {
          setOptions(comments);
        }
      } catch (error) {
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: { error },
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [show, resolutionStatus, activeBenchmark, dispatchErrors]);

  const onSubmit = async (formData: ResolutionForm) => {
    const req: ResolveBenchmarkCompanyRequest = {
      status: resolutionStatus,
      comment: formData.resolutionComment,
      applyForRemaining: formData.applyForRemaining,
    };
    setDisabled(true);
    try {
      if (
        company?.companyInfo?.id &&
        (await resolveCompany(activeBenchmark?.id!, company?.companyInfo?.id, req))
      ) {
        handleSave();
      }
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    } finally {
      setDisabled(false);
    }
  };

  return (
    <Box>
      <Dialog
        disableRestoreFocus={true}
        onClose={() => {
          handleClose();
        }}
        open={show}
        className={classes.dialogBox}
      >
        <DialogTitleBlock
          icon={<TuneIcon />}
          title={title}
          handleClose={handleClose}
          disabled={disabled}
        />
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              render={({ onChange, ...props }) => (
                <Autocomplete
                  freeSolo
                  loading={loading}
                  options={options}
                  autoSelect
                  renderInput={(params) => {
                    params.InputProps = {
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                      className: '', // className in InputProps is MuiAutocomplete-inputRoot. It renders too big input box
                    };
                    return (
                      <>
                        <TextField
                          {...params}
                          label={t('benchmark:resolveBenchmarkCompany.commentField')}
                          variant="outlined"
                          margin="normal"
                          error={!!errors.resolutionComment}
                          autoFocus
                        />
                        <Box style={{ color: 'red' }}>
                          <Typography variant="caption">
                            {t(errors.resolutionComment?.message || '')}
                          </Typography>
                        </Box>
                      </>
                    );
                  }}
                  onChange={(e: React.ChangeEvent<{}>, value: string | null) => onChange(value)}
                  onOpen={() => clearErrors('resolutionComment')}
                  {...props}
                />
              )}
              onChange={(data: string) => data}
              defaultValue={isEditing ? comment : null}
              name="resolutionComment"
              control={control}
            />
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={disabled}
              className={classes.button}
            >
              {t('app:save')}
            </Button>
            {!isEditing && (
              <InputCheckbox
                label={t('benchmark:resolveBenchmarkCompany.applyForRemaining')}
                name="applyForRemaining"
                defaultValue={false}
                refReg={register}
              />
            )}
          </form>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const validationSchema = (resolutionStatus: ResolveBenchmarkCompanyRequest.StatusEnum) => {
  const validationFields =
    resolutionStatus === 'REJECTED'
      ? {
          resolutionComment: yup.string().nullable().required('companies:requiredField'),
        }
      : {};
  return yup.object().shape(validationFields);
};

const useStyles = makeStyles((theme) => ({
  dialogBox: {
    '& .MuiDialogContent-root .MuiPaper-root': {
      borderRadius: 0,
    },
    '& .MuiPaper-root': {
      minWidth: '500px',
    },
  },
  button: {
    marginRight: theme.spacing(5),
  },
}));

export default ResolutionModal;
